import { useTranslation } from "@i18n";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import Text from "@CubeComponents/html/Text/Text";

/**
 * Warning shown before a cut/paste that would break external links.
 *
 * Only raised when the moved items THEMSELVES carry active links. Moving something into or out of a
 * linked folder is not a break — a folder link reflects live contents — so that case stays silent,
 * and this dialog never appears for it.
 *
 * Cancel is a real cancel: the move has not started when this is on screen.
 */
type MoveBreaksLinksModalProps = {
  linkCount: number;
  itemCount: number;
  onConfirm: () => void;
  onCancel: () => void;
};

function MoveBreaksLinksModal({ linkCount, itemCount, onConfirm, onCancel }: Readonly<MoveBreaksLinksModalProps>) {
  const { t } = useTranslation();

  return (
    <ModalStateDialogComponent
      isVisible
      width="29rem"
      headerTitle={t("Move items")}
      onCancel={onCancel}
      modelContent={
        <div className="flex flex-col gap-3 py-2">
          <Text className="text-sm">{t("Are you sure you want to move this item?")}</Text>
          <div className="rounded-md border border-warning bg-bg_warning px-3 py-2">
            <Text noWrap className="text-xs leading-relaxed">
              {t(
                "{{itemCount}} of the selected items are shared through {{linkCount}} active external link(s). External links point to a fixed location, so moving them will break those links and recipients will see an invalid-link page.",
                { itemCount, linkCount },
              )}
            </Text>
          </div>
        </div>
      }
      footerContent={
        <div className="flex gap-3">
          <ButtonComponent buttonType={BUTTON_TYPES.GRADIENT_BUTTON} onClick={onConfirm}>
            {t("Confirm")}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={onCancel}>
            {t("Cancel")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

export default MoveBreaksLinksModal;
